import { useCallback } from "react";

import { useHistoryTracker } from "../../../history";
import { useTransitionRouter } from "../../../transition-router";
import { CloseConfirmationModal } from "../close-confirmation-modal";
import type { ModalCloseSource } from "../modal-behavior";
import type { ModalComponent } from "../modal-builder";
import { type ModalWithOptionalDeduplicationKey, resolveModalId } from "../modal-id";
import { useRouteModalsStore } from "../route-modals.store";
import { useUserTriggeredModalsStore } from "../user-triggered-modals.store";
import { useModalOpenOperations } from "./use-modal-open-operations";
import { useModalSetOperations } from "./use-modal-set-operations";

export const useModalCloseOperations = () => {
  const router = useTransitionRouter();

  const { previousUrl, currentUrl } = useHistoryTracker();

  const isRouteModalMounted = useRouteModalsStore((state) => state.isMounted);
  const routeModalFallbackPath = useRouteModalsStore((state) => state.fallbackPath);

  const triggerRouteModalCloseCallback = useRouteModalsStore((state) => state.triggerCloseCallback);
  const getRouteModalShouldConfirmClose = useRouteModalsStore((state) => state.getShouldConfirmClose);
  const triggerUserTriggeredModalCloseCallback = useUserTriggeredModalsStore((state) => state.triggerCloseCallback);
  const getShouldConfirmClose = useUserTriggeredModalsStore((state) => state.getShouldConfirmClose);

  const peekOpenIds = useUserTriggeredModalsStore((state) => state.peekOpenIds);
  const clearState = useUserTriggeredModalsStore((state) => state.clearState);

  const { setUserTriggeredModals } = useModalSetOperations();
  const { openUserTriggeredModal } = useModalOpenOperations();

  const closeUserTriggeredModalById = useCallback(
    (id: string, source: ModalCloseSource) => {
      const openModalIds = peekOpenIds();
      if (openModalIds.length === 0) {
        return;
      }

      if (source === "user") {
        if (getShouldConfirmClose(id)) {
          openUserTriggeredModal(CloseConfirmationModal, { triggererId: id });
          return;
        }

        const closeOutcome = triggerUserTriggeredModalCloseCallback(id);
        if (closeOutcome?.shouldKeepModalOpen) {
          return;
        }
      }

      const expectedModalIds = openModalIds.filter((modalId) => modalId !== id);
      if (expectedModalIds.length === openModalIds.length) {
        return;
      }

      clearState(id);
      setUserTriggeredModals(expectedModalIds);
    },
    [
      peekOpenIds,
      setUserTriggeredModals,
      triggerUserTriggeredModalCloseCallback,
      getShouldConfirmClose,
      openUserTriggeredModal,
      clearState,
    ],
  );

  const closeUserTriggeredModal = useCallback(
    (modal: ModalWithOptionalDeduplicationKey<ModalComponent<never>>) => {
      closeUserTriggeredModalById(resolveModalId(modal), "code");
    },
    [closeUserTriggeredModalById],
  );

  const closeTopmostModal = useCallback(
    (source: ModalCloseSource) => {
      const openModalIds = peekOpenIds();
      const hasModals = isRouteModalMounted || openModalIds.length > 0;
      if (!hasModals) {
        return;
      }

      if (openModalIds.length === 0) {
        if (source === "user") {
          if (getRouteModalShouldConfirmClose()) {
            openUserTriggeredModal(CloseConfirmationModal, {
              isRouteModal: true,
            });
            return;
          }

          const closeOutcome = triggerRouteModalCloseCallback();
          if (closeOutcome?.shouldKeepModalOpen) {
            return;
          }
        }

        if (previousUrl && previousUrl !== currentUrl) {
          window.history.back();
        } else {
          router.push(routeModalFallbackPath);
        }
      } else {
        const topmostId = openModalIds[openModalIds.length - 1]!;

        closeUserTriggeredModalById(topmostId, source);
      }
    },
    [
      previousUrl,
      peekOpenIds,
      triggerRouteModalCloseCallback,
      routeModalFallbackPath,
      router,
      isRouteModalMounted,
      closeUserTriggeredModalById,
      currentUrl,
      getRouteModalShouldConfirmClose,
      openUserTriggeredModal,
    ],
  );

  const closeAllUserTriggeredModals = useCallback(() => setUserTriggeredModals([]), [setUserTriggeredModals]);

  return {
    closeUserTriggeredModal,
    closeUserTriggeredModalById,
    closeTopmostModal,
    closeAllUserTriggeredModals,
  };
};
